import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowLeft, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CFI_LOGO = "/images/cfi-logo-main.png";

const UpdateEmail = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter old email, 2: Verify, 3: Success
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    oldEmail: "",
    newEmail: "",
    verificationCode: "",
  });
  const [requestId, setRequestId] = useState(null);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRequestChange = async (e) => {
    e.preventDefault();

    if (!formData.oldEmail || !formData.newEmail) {
      toast.error("Please fill in both email addresses");
      return;
    }

    if (formData.oldEmail === formData.newEmail) {
      toast.error("New email must be different from old email");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/auth/request-email-change`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          old_email: formData.oldEmail,
          new_email: formData.newEmail,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Verification code sent to your old email address");
        setRequestId(data.request_id);
        setStep(2);
      } else {
        toast.error(data.detail || "Failed to process request");
      }
    } catch (error) {
      console.error("Email change error:", error);
      toast.error("Failed to process request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyChange = async (e) => {
    e.preventDefault();

    if (!formData.verificationCode) {
      toast.error("Please enter the verification code");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/auth/confirm-email-change`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          request_id: requestId,
          verification_code: formData.verificationCode,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Email updated successfully!");
        setStep(3);
      } else {
        toast.error(data.detail || "Invalid verification code");
      }
    } catch (error) {
      console.error("Verification error:", error);
      toast.error("Failed to verify. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f8f3] flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1a3d18] to-[#265324]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/cfi">
              <img src={CFI_LOGO} alt="Credit Firm Inc" className="h-12 w-auto" />
            </Link>
            <Link to="/login">
              <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md">
          {step === 1 && (
            <>
              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-[#779a86] flex items-center justify-center">
                  <Mail className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-[#1a3d18]">Update Your Email</CardTitle>
                <CardDescription>
                  Has your email changed? We'll help you update it so you can continue accessing your account.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <form onSubmit={handleRequestChange} className="space-y-4">
                  <div>
                    <Label htmlFor="oldEmail">Current Email (the one on file)</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="oldEmail"
                        type="email"
                        value={formData.oldEmail}
                        onChange={(e) => handleChange("oldEmail", e.target.value)}
                        placeholder="old@company.com"
                        className="pl-10"
                        required
                        data-testid="old-email-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      This is the email you originally registered with
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="newEmail">New Email Address</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="newEmail"
                        type="email"
                        value={formData.newEmail}
                        onChange={(e) => handleChange("newEmail", e.target.value)}
                        placeholder="new@company.com"
                        className="pl-10"
                        required
                        data-testid="new-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#357c2a] hover:bg-[#265324] text-white"
                    data-testid="request-change-btn"
                  >
                    {isSubmitting ? "Processing..." : "Send Verification Code"}
                  </Button>
                </form>

                <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> A verification code will be sent to your <strong>old email address</strong> to confirm this change. 
                    If you no longer have access to your old email, please contact support.
                  </p>
                </div>
              </CardContent>
            </>
          )}

          {step === 2 && (
            <>
              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-[#357c2a] flex items-center justify-center">
                  <Send className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-[#1a3d18]">Enter Verification Code</CardTitle>
                <CardDescription>
                  We sent a 6-digit code to <strong>{formData.oldEmail}</strong>. Enter it below to confirm your email change.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <form onSubmit={handleVerifyChange} className="space-y-4">
                  <div>
                    <Label htmlFor="verificationCode">Verification Code</Label>
                    <Input
                      id="verificationCode"
                      value={formData.verificationCode}
                      onChange={(e) => handleChange("verificationCode", e.target.value)}
                      placeholder="123456"
                      className="text-center text-2xl tracking-widest mt-1"
                      maxLength={6}
                      required
                      data-testid="verification-code-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#357c2a] hover:bg-[#265324] text-white"
                    data-testid="verify-code-btn"
                  >
                    {isSubmitting ? "Verifying..." : "Verify & Update Email"}
                  </Button>
                </form>

                <button
                  onClick={() => setStep(1)}
                  className="w-full mt-4 text-sm text-slate-500 hover:text-[#357c2a]"
                >
                  ← Go back and try different email
                </button>
              </CardContent>
            </>
          )}

          {step === 3 && (
            <>
              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-[#84c77f] flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-[#265324]" />
                </div>
                <CardTitle className="text-2xl text-[#1a3d18]">Email Updated!</CardTitle>
                <CardDescription>
                  Your email has been successfully changed to <strong>{formData.newEmail}</strong>. You can now use this email to sign in.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <Link to="/login">
                  <Button className="w-full bg-[#357c2a] hover:bg-[#265324] text-white">
                    Go to Login
                  </Button>
                </Link>
              </CardContent>
            </>
          )}
        </Card>
      </main>
    </div>
  );
};

export default UpdateEmail;
